import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { faroTableComparator } from "@components/common/faro-table/faro-table-utils";
import { BasicInfoMemberColumn } from "@components/table/members/members-column/basic-info-member-column";
import { RoleColumn } from "@components/table/members/members-column/role-column/role-column";
import { StatusColumn } from "@components/table/members/members-column/status-column/status-column";
import {
  MembersTableColumnField,
  MembersTableGridColDef,
} from "@components/table/members/members-table-types";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { Box, Stack } from "@mui/material";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { isGroupMember } from "@custom-types/member-type-guards";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useDateTime } from "@hooks/use-date-time";
import {
  GridRenderCellParams,
  gridStringOrNumberComparator,
} from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { withEllipsis } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";
import { isTeam } from "@utils/team-utils";
import { DASH } from "@utils/ui-utils";
import { getMemberStatus } from "@utils/user-utils";
import { MEMBERS_TABLE_COLUMN_LABELS } from "@components/table/members/members-table-constants";
import { isMembersTableColumnField } from "@custom-types/type-guards";
import { useMediaQueryList } from "@hooks/use-media-query";
import { MemberOptionsMenu } from "@components/table/members/member-options-menu";

interface Props extends BaseCompanyIdProps {
  /** The selected project if there is one */
  project?: SdbProject | null;

  /** Recognizes on which subject the table is being used */
  tableSubject: MemberTableSubject;

  /** The selected group if there is one */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** The selected team if there is one */
  team?: FaroCoreApiTypes.Team;
}

/** Minimum column width in px */
const COLS_MIN_WIDTH_IN_PX = 150;

/**
 * Columns width in xs and sm screen
 */
const xsAndSmColumnsWidthPX = 200;

/**
 * like name, email, status and joined date in medium screens
 */
const mdColumnsWidthInPX = 250;

/** Return all the possible columns for members table */
export function useMembersColumns({
  companyId,
  project,
  tableSubject,
  group,
  team,
}: Props): MembersTableGridColDef[] {
  const { columnPreferences, visibleOrderedColumns } =
    useColumnPreferencesContext<MembersTableColumnField>();
  const { isScreenMd, isScreenLgOrLarger } = useMediaQueryList();
  const { formatDate } = useDateTime();

  const columnsMap: Record<MembersTableColumnField, MembersTableGridColDef> = {
    name: {
      field: "name",
      headerName: MEMBERS_TABLE_COLUMN_LABELS.name,
      flex: 1,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      disableReorder: true,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => (
        <Stack
          direction="row"
          alignItems="center"
          gap="0.625rem"
          sx={{ ...withEllipsis }}
        >
          <UserAvatarWithMenu member={params.row.entity} />
          <BasicInfoMemberColumn
            member={params.row.entity}
            columnType={MemberHeaders.name}
          />
        </Stack>
      ),
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity.name,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: tableSubject !== "team",
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },
    user: {
      field: "user",
      headerName: MEMBERS_TABLE_COLUMN_LABELS.user,
      flex: 1,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => (
        <Stack
          direction="row"
          alignItems="center"
          gap="0.625rem"
          sx={{ ...withEllipsis }}
        >
          <UserAvatarWithMenu member={params.row.entity} />
          <BasicInfoMemberColumn
            member={params.row.entity}
            columnType={MemberHeaders.user}
          />
        </Stack>
      ),
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity.name,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: tableSubject !== "team",
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      disableReorder: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },
    email: {
      field: "email",
      flex: isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={MEMBERS_TABLE_COLUMN_LABELS.email}
        />
      ),
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => (
        <BasicInfoMemberColumn
          member={params.row.entity}
          columnType={MemberHeaders.email}
        />
      ),
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: tableSubject !== "team",
      sortComparator: (v1, v2, cp1, cp2) => {
        const teamComparison = Number(isTeam(v1)) - Number(isTeam(v2));

        if (teamComparison !== 0) {
          return teamComparison;
        }

        return gridStringOrNumberComparator(v1.email, v2.email, cp1, cp2);
      },
    },

    status: {
      field: "status",
      flex: isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon title={MemberHeaders.status} />
      ),
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        return isTeam(params.row.entity) ? (
          <var>{DASH}</var>
        ) : (
          <StatusColumn
            companyId={companyId}
            project={project}
            subjectType={tableSubject}
            member={params.row.entity}
          />
        );
      },
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: tableSubject !== "team",
      sortComparator: (v1, v2, cp1, cp2) => {
        const status1 = getMemberStatus({ member: v1 });
        const status2 = getMemberStatus({ member: v2 });

        // Ensures the result is numeric
        const teamComparison = Number(isTeam(v1)) - Number(isTeam(v2));

        if (teamComparison !== 0) {
          return teamComparison;
        }

        return gridStringOrNumberComparator(status1, status2, cp1, cp2);
      },
    },

    role: {
      field: "role",
      flex: isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      maxWidth: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={MEMBERS_TABLE_COLUMN_LABELS.role}
        />
      ),
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        return (
          <RoleColumn
            companyId={companyId}
            selectedProject={project}
            subjectType={tableSubject}
            memberIdentityRole={{
              identity: params.row.entity.identity,
              role: params.row.entity.role,
            }}
            group={group}
          />
        );
      },
      valueGetter: (_, row: { entity: SdbMemberTypes }) =>
        formatUserRoleType(row.entity.role),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: tableSubject !== "team",
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    joinedDate: {
      field: "joinedDate",
      flex: isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      width: isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={MEMBERS_TABLE_COLUMN_LABELS.joinedDate}
        />
      ),
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        if (isGroupMember(params.row.entity)) {
          return (
            <Box component="var" sx={withEllipsis}>
              {formatDate(params.row.entity.dateJoined)}
            </Box>
          );
        }
      },
      valueGetter: (_, row: { entity: SdbMemberTypes }) =>
        isGroupMember(row.entity) ? row.entity.dateJoined : undefined,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    options: {
      field: "options",
      type: "actions",
      align: "right",
      width: 90,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      disableReorder: true,
      renderHeader: () => (
        <FaroTableColumnVisibilityMenuIcon
          columnPreferences={columnPreferences}
        />
      ),
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        return (
          <MemberOptionsMenu
            selectedProject={project}
            subjectType={tableSubject}
            member={params.row.entity}
            group={group}
            team={team}
          />
        );
      },
    },
  };

  // Return only visible columns in the order specified by preferences
  return visibleOrderedColumns
    .filter(isMembersTableColumnField)
    .map((field) => columnsMap[field]);
}
